// Retry policy for React Query, tuned for one specific failure: the reconnect
// race after the laptop wakes.
//
// When the machine sleeps and wakes, the browser fires an `online` event and
// React Query (refetchOnReconnect defaults to true) refetches mounted queries.
// For a beat, the browser reports "online" while end-to-end connectivity
// (DNS / TLS / VPN) isn't actually ready — so `fetch` throws and `api.ts`
// turns it into `ApiError(0, "NETWORK_ERROR")`. With `retry: false` the query
// went straight to `isError`, and pages that do `if (isError) return
// <ErrorComponent>` threw away good cached data and showed "Unable to reach
// the server" until a manual refresh.
//
// We retry ONLY that transient, connectivity-level failure — never a real HTTP
// status. A 4xx/5xx still fails fast exactly as before, so existence probes and
// permission checks are unaffected. By the time the short backoff elapses the
// network is up and the refetch succeeds, so the error view never appears.

/**
 * True only for connectivity-level failures where `fetch` itself threw — not
 * for any HTTP response (4xx/5xx come back with a real status and are
 * intentionally NOT treated as transient).
 *
 * `api.ts` models a thrown fetch as `ApiError(0, "NETWORK_ERROR")`; we match it
 * structurally on `code` rather than by `instanceof` so the check survives
 * bundle boundaries and stays free of the `@/lib/api` import chain (which lets
 * this module be unit-tested under plain `node:test`). `NETWORK_ERROR` is only
 * ever set here, so the structural match is exact.
 */
export function isTransientNetworkError(error: unknown): boolean {
  // Non-`request()` callers (better-auth, integrations client) may surface a
  // raw fetch TypeError instead of going through our wrapper.
  if (error instanceof TypeError) return /fetch|network/i.test(error.message);
  if (typeof error === "object" && error !== null && "code" in error) {
    return (error as { code?: unknown }).code === "NETWORK_ERROR";
  }
  return false;
}

/** How many attempts total (initial try + retries) we make for a transient blip. */
export const MAX_QUERY_ATTEMPTS = 3;

/**
 * React Query `retry` callback. `failureCount` is the number of failures so
 * far (1 after the first). Retry a transient blip until we've made
 * MAX_QUERY_ATTEMPTS attempts; never retry anything else.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  return isTransientNetworkError(error) && failureCount < MAX_QUERY_ATTEMPTS;
}

/** Exponential backoff capped at 4s: ~1s, then ~2s. Total added wait ≈ 3s. */
export function queryRetryDelay(attemptIndex: number): number {
  return Math.min(1000 * 2 ** attemptIndex, 4000);
}
